import type { ReactNode } from "react";

import { addVariantBlock, detailRows, type CatalogDetailItem } from "@/lib/catalog-item-detail";
import { useItemsError } from "@/hooks/items-error";
import type { CatalogItemImageInput } from "@/lib/catalog-item-image";
import { dimensionVariantRow, finishVariantRow } from "@/lib/catalog-item-variants";
import { ItemDimensionVariants, ItemFinishVariants } from "./item-variant-chips";
import { ItemDetailStatusBadge, ItemEiTierChips } from "./item-badges";
import { ItemDetailActions, ItemReadOnlyActions } from "./item-detail-actions";
import { ItemDetailTable, ItemSpecUsageRows, type CatalogDivisionUsage } from "./item-detail-table";
import { ItemDetailImages } from "./item-gallery";
import {
  AddVariantButton,
  DetailSkeleton,
  ItemDescriptionSection,
  ItemDetailLayout,
  ItemMetaHeading,
  ManufacturerSiteButton,
  SectionBand,
} from "./item-detail-layout";

// The item details page — `ItemDetailsIsland`, the donor's composition of the
// blocks each of the files beside this one ports.
//
// **The authed composition.** The frame it stands in — the page padding, the
// header row, the two-column grid, the band and the skeleton — is shared with
// the anonymous product page and lives in `item-detail-layout.tsx`; the donor
// has one island for both audiences and switches its *contents* on `isPublic`.
// What is left here is that content, on the authed side of every gate.
//
// The page's own decisions, as opposed to its parts':
//
//   - **Failures are snackbars, not page states.** All three — the item read,
//     the usage read, the delete — are the donor's `ItemsError`: bottom-centre,
//     dismissible, ten seconds, over a page that still renders. The same
//     idiom, and the same reasoning, as `request-item-dialog.tsx`.

/** `constants.ts` — the donor's two error strings, verbatim. */
const LOAD_ITEM_ERROR = "Error loading item details.";
const LOAD_SPEC_USAGE_ERROR = "Error updating the item specification counts";
export const DELETE_ITEM_ERROR = "Error deleting item. Click on the 'Delete' button to try again.";

interface ItemDetailContentProps {
  item: CatalogDetailItem | null;
  /**
   * The item's images, hero first — empty for an item with none.
   *
   * A sibling of `item` rather than a field on it, because that is where it sits
   * on the wire: `ItemDetailOutput` carries `images` beside `item`, not inside it
   * (`server/app/api/contracts/catalog.py`). Folding it into
   * `CatalogDetailItem` would put a field on the summary type that the *list*
   * read never sends.
   */
  images?: readonly CatalogItemImageInput[];
  /**
   * The Total Specifications breakdown: the Core count and the named division
   * rows. Siblings of `item` for the same reason `images` is — the detail
   * response carries them beside the item, and the list's summary never does.
   */
  coreUsageCount?: number;
  divisionUsage: readonly CatalogDivisionUsage[];
  /** The breakdown's read has landed for the displayed item — see `ItemSpecUsageRows`. */
  breakdownArrived?: boolean;
  /** The item read has not landed yet — the skeleton's condition. */
  isLoading: boolean;
  /** The item read failed with nothing to show. */
  isError: boolean;
  /**
   * The usage counts are being re-read.
   *
   * They ride the item read here rather than the donor's second query, so this
   * is that read refetching over data already on screen — which is exactly when
   * the donor shows its spinner in the count cell and in the action menu.
   */
  isUsageLoading?: boolean;
  /** The re-read failed while the item itself is still on screen. */
  isUsageError?: boolean;
  onDismissUsageError?: () => void;
  /**
   * The user asked for the usage — opened the ⋮ or the Spec Usage breakdown,
   * the two presses the donor's usage read fires on.
   */
  onUsageAsked?: () => void;
  /**
   * The displayed item's variant group — every alive member, the displayed one
   * included.
   *
   * It arrives on the same read as the item (`itemDetailResponseWire.variants`,
   * typed as the *same* `itemSummaryWire`), which is why the two rows below need
   * no read of their own and why switching variants costs no request. Empty for
   * an ungrouped item, which still draws both headings — see the rows themselves.
   */
  variants?: readonly CatalogDetailItem[];
  /** Called with the item id a variant chip switches the page to. */
  onSelectVariant?: (itemId: number) => void;
  /**
   * What stands in for the item while `item` is null — the partial the spec
   * page's item dialog is opened with (`ItemDialogSeed`).
   *
   * The donor's dialog seeds `{id, name, heroImgUrl}` into its item cache
   * (`observers.ts:50-64`), so during the read the island has an item to draw
   * from: the name as its `h4`, the hero as the gallery's one image, and the ⋮,
   * gated only on `!item` (`Header/ActionMenu.tsx:72`). What the partial lacks
   * stays out — no badges (no approval, core flag or tiers), and the
   * manufacturer line holds its height empty. The page route has no seed and so
   * draws nothing there, as the donor's page does.
   *
   * `item` is the cache's own copy, when the opener holds one — the Item Search
   * island's card, or its loaded copy of a row's item (#964 N16) — and then
   * the badges and the manufacturer draw from it too, as the donor's dialog
   * draws them from the object cache the search filled.
   */
  placeholder?: {
    id: number;
    name: string;
    images: readonly CatalogItemImageInput[];
    item?: CatalogDetailItem | null;
  };
  /**
   * The island's dialog mode with verbs (#798): no page frame, and the menu's
   * dialog arrangement — Edit in a new tab, and Report between Edit and Copy
   * Link (`Header/ActionMenu.tsx`, `renderingMode === 'dialog'`). The item
   * page route passes nothing.
   */
  inDialog?: boolean;
  /**
   * The Active selection's line is finalized: the finish and dimension rows
   * and the action menu are not drawn (`FinishVariants.tsx`,
   * `DimensionVariants.tsx`, `Header/ActionMenu.tsx:70-74`). Only the
   * island's dialog passes it.
   */
  finalized?: boolean;
  /**
   * `Select` / `Remove Selected`, above Add Variant — the island's half of
   * `CtaButtons`. The item page route passes none.
   */
  selectionCta?: ReactNode;
  /**
   * The item History drawer's `View History` trigger (#1179; ADR-0028
   * decision 14), drawn immediately before the ⋮ once the item has loaded.
   * Only the item page route passes it: the anonymous product page, the spec
   * page's read-only dialog and the Item Search dialog pass nothing, and their
   * header row is as it was.
   */
  history?: ReactNode;
}

/** The page's verbs — the action menu's, and Add Variant's. */
interface ItemDetailVerbProps {
  /** Whether this user passes the item edit ladder. */
  canEdit: boolean;
  /** The confirmed delete — absent when the host confirms (`onDeleteRequested`). */
  onDelete?: () => void;
  isDeleting?: boolean;
  deleteFailed?: boolean;
  onDismissDeleteError?: () => void;
  onCopyLink: () => void;
  onAddVariant?: () => void;
  /**
   * The Delete row was pressed, and the host confirms the delete itself — the
   * island's dialog, which closes on the press (its donor's observer listens
   * to the click) and so cannot hold the confirmation inside itself. Absent,
   * the menu confirms and calls `onDelete`.
   */
  onDeleteRequested?: () => void;
}

/**
 * The details page with its verbs, or the island's `readOnly` dialog mode —
 * the spec details page's item dialog (#695): no page frame, the Report-only
 * menu, and no CTAs (`!readOnly` gates them, `ItemDetailsIsland.tsx:179`), so
 * no verbs to pass.
 */
type ItemDetailPageProps = ItemDetailContentProps &
  ((ItemDetailVerbProps & { readOnly?: false }) | { readOnly: true });

export function ItemDetailPage(props: ItemDetailPageProps) {
  const {
    item,
    images = [],
    coreUsageCount = 0,
    divisionUsage,
    breakdownArrived = true,
    isLoading,
    isError,
    isUsageLoading = false,
    isUsageError = false,
    onDismissUsageError,
    onUsageAsked,
    variants = [],
    onSelectVariant,
    placeholder,
    readOnly = false,
    inDialog = false,
    finalized = false,
    selectionCta,
    history,
  } = props;
  // The partial stands in only until the item arrives.
  const standIn = item ? undefined : placeholder;
  // What the header draws from: the item, else the opener's copy of it.
  const header = item ?? standIn?.item ?? null;
  const verbs = props.readOnly ? null : props;

  // Both rows are derived, not fetched: the gate and the chips are one decision
  // per row and both live in `@/lib/catalog-item-variants` under unit test. `null`
  // means the row does not draw at all.
  const finishRow = item && !finalized ? finishVariantRow(item, variants) : null;
  const dimensionRow = item && !finalized ? dimensionVariantRow(item, variants) : null;

  useItemsError(isError, LOAD_ITEM_ERROR, {
    // **Permanent, because the donor's is.** `ItemDetailsIsland.tsx:192-194`
    // renders `<ItemsError error={ERRORS.LOAD_ITEM} />` with no `onClose`, and
    // `ItemsError`'s `autoHideDuration` default of 10000 is inert without one:
    // MUI's Snackbar implements auto-hide *by calling* `onClose`, and
    // `Notification`'s own handler returns early unless one was passed
    // (`react-ui/src/notification/notification.tsx:59-67`), while `open` is a
    // hardcoded literal. Clickaway and Escape are refused there too and there
    // is no close button, so a failed item read leaves the snackbar up for the
    // life of the page.
    //
    // #309's parity floor says "auto-hiding after 10s", which reads the
    // declared duration rather than its effect. Found by cross-review run 3.
    duration: Infinity,
  });
  useItemsError(isUsageError, LOAD_SPEC_USAGE_ERROR, { onClose: onDismissUsageError });
  useItemsError(verbs?.deleteFailed ?? false, DELETE_ITEM_ERROR, {
    onClose: verbs?.onDismissDeleteError,
  });

  return (
    <ItemDetailLayout
      framed={!readOnly && !inDialog}
      badges={
        header && (
          <>
            <ItemDetailStatusBadge item={header} />
            {/* Suppressed while pending — `itemEiTiers`' gate, which is the
              donor's `!showPendingBadge` on the same row. And authed-only:
              `Badges.tsx:21` gates the chips on `!isPublic`. */}
            <ItemEiTierChips item={header} />
          </>
        )
      }
      actions={
        <HeaderActions history={item && !finalized ? history : null}>
          {/* `!item` draws nothing at all, so a page still loading shows no menu
          rather than a menu with nothing to act on — but a stand-in is an
          item to the donor's ⋮ (`ActionMenu.tsx:72`, #964 N16). */}
          {finalized ? null : verbs ? (
            <ItemDetailActions
              item={header ?? (standIn ? { id: standIn.id } : null)}
              canEdit={verbs.canEdit}
              isLoadingUsage={isUsageLoading}
              breakdownArrived={breakdownArrived}
              onDelete={verbs.onDelete}
              isDeleting={verbs.isDeleting}
              onCopyLink={verbs.onCopyLink}
              onAddVariant={verbs.onAddVariant}
              onDeleteRequested={verbs.onDeleteRequested}
              inDialog={inDialog}
              onMenuOpen={onUsageAsked}
            />
          ) : (
            <ItemReadOnlyActions visible={Boolean(item ?? standIn)} />
          )}
        </HeaderActions>
      }
      image={
        /* The gallery, the three arms behind it and the EI mark anchored to
          them — #303 replaced the placeholder-only stub #251 stood here. */
        standIn ? (
          // The partial is approved as far as the gallery can tell: the donor's
          // pending overlay asks `isApproved === false`, and the partial has no
          // `isApproved` at all. Nor any tiers, so no EI mark either.
          <ItemDetailImages
            item={
              standIn.item
                ? { ...standIn.item, name: standIn.name }
                : { name: standIn.name, isApproved: true, isCore: false }
            }
            images={standIn.images}
          />
        ) : (
          <ItemDetailImages item={item} images={images} />
        )
      }
      meta={
        <>
          <ItemMetaHeading
            manufacturerName={header?.manufacturer.name}
            itemName={item?.name ?? standIn?.name}
          />

          {/* The donor's own condition, and only it (`ItemDetailsIsland
            .tsx:120-127` — `isLoadingItem || isFetchingItem`, which both routes
            reach through `itemDetailIsLoading`). A *failed* read is not a
            loading one, so the page below still draws, empty, under the error
            snackbar rather than holding three skeleton blocks up forever. */}
          {isLoading ? (
            <DetailSkeleton />
          ) : (
            <>
              {/* Inside the loading branch and above the first band, which is
                where the donor puts them (`ItemDetailsIsland.tsx:154-173`):
                while the read is in flight there is no finish to name and no
                group to chip, so the skeleton stands for these too.

                Authed-only — `ItemDetailsIsland.tsx:160` gates both rows on
                `!isPublic`. */}
              <ItemFinishVariants
                row={finishRow}
                onSelect={(itemId) => onSelectVariant?.(itemId)}
              />
              <ItemDimensionVariants
                row={dimensionRow}
                onSelect={(itemId) => onSelectVariant?.(itemId)}
              />

              {/* `CtaButtons` — the donor's `if (!item) return null`. */}
              {item && selectionCta}
              {item && verbs?.onAddVariant && (
                <AddVariantButton
                  disabled={addVariantBlock(item) !== null}
                  onClick={verbs.onAddVariant}
                />
              )}

              <SectionBand />

              {/* The donor's `<Details>{!isPublic && <SpecUsage/>}</Details>`
                (`ItemDetailsIsland.tsx:181-183`). This is the authed side of
                that gate; the anonymous product page fills the slot with
                nothing. */}
              <ItemDetailTable rows={item ? detailRows(item) : []}>
                <ItemSpecUsageRows
                  item={item}
                  coreUsageCount={coreUsageCount}
                  divisionUsage={divisionUsage}
                  breakdownArrived={breakdownArrived}
                  usageState={isUsageError ? "error" : isUsageLoading ? "loading" : "ready"}
                  onExpand={onUsageAsked}
                />
              </ItemDetailTable>

              <ManufacturerSiteButton href={item?.url} />

              <SectionBand />

              <ItemDescriptionSection html={item?.description} />
            </>
          )}
        </>
      }
    />
  );
}

/**
 * The header row's right-hand side: the ⋮ alone, as every caller but the item
 * page route draws it, or the History trigger then the ⋮. The donor's own row
 * there is a `Stack direction='row' alignItems='center'` holding only the ⋮
 * (`ItemDetailsIsland.tsx:139`); the 8px between the two is the `spacing={1}`
 * Modsy puts beside the same trigger in the spec header
 * (`SpecHeaderIsland.tsx:75`).
 */
function HeaderActions({ history, children }: { history: ReactNode; children: ReactNode }) {
  if (!history) return <>{children}</>;
  return (
    <div className="flex items-center gap-2">
      {history}
      {children}
    </div>
  );
}
